import { zodResolver } from '@hookform/resolvers/zod';
import { Group, Stack, Text } from '@mantine/core';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { CitationPrestigeSelectControl } from '@/module/Author/Citation/components/form/CitationPrestigeSelectControl';
import { JournalSelectControl } from '@/module/Nomen/Journal/JournalSelectControl';
import { PublisherSelectControl } from '@/module/Nomen/Publisher/PublisherSelectControl';
import { WosCategoryMultiSelectControl } from '@/module/Nomen/WosCategory/WosCategoryMultiSelectControl';
import { WosIndexMultiSelectControl } from '@/module/Nomen/WosIndex/WosIndexMultiSelectControl';

import {
  Content,
  EnumSelectControl,
  Form,
  FormSubmit,
  NumberInputControl,
  PublishedAtControl,
  TextInputControl,
} from '@/core/components';

import { CitationSchema } from './model/CitationSchema';
import { PublishedIn } from './model/CitationTypes';

interface CitationFormProps extends FormSubmit<CitationSchema> {
  title: string;
  showWosFields: boolean;
  showScopusFields: boolean;
}

export function CitationForm(props: CitationFormProps) {
  const { title, schema, defaultValues, values, isSubmitting, onCancel, onSubmit } = props;
  const { t } = useTranslation();
  const form = useForm<CitationSchema>({
    defaultValues,
    values,
    resolver: zodResolver(schema),
  });
  const { watch } = form;
  const publishedIn = watch('publishedIn');

  return (
    <FormProvider {...form}>
      <Form
        title={title}
        isSubmitting={isSubmitting}
        onCancel={onCancel}
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <Stack>
          <Content type="full">
            <TextInputControl
              name="title"
              label={t('citations.form.fields.title')}
              isSubmitting={isSubmitting}
            />
            <Group align="flex-end">
              <EnumSelectControl
                name="publishedIn"
                label={t('citations.form.fields.publishedIn')}
                isSubmitting={isSubmitting}
                options={[
                  { value: PublishedIn.JOURNAL, label: t('citations.form.fields.journal') },
                  { value: PublishedIn.PUBLISHER, label: t('citations.form.fields.publisher') },
                ]}
              />
              {publishedIn === PublishedIn.PUBLISHER && (
                <PublisherSelectControl
                  name="publisher"
                  label={t('citations.form.fields.publisher')}
                  isSubmitting={isSubmitting}
                />
              )}
              {publishedIn === PublishedIn.JOURNAL && (
                <JournalSelectControl
                  name="journal"
                  label={t('citations.form.fields.journal')}
                  isSubmitting={isSubmitting}
                />
              )}
            </Group>
            <Stack>
              <Group>
                <TextInputControl
                  leftSectionWidth="80px"
                  leftSection={<Text fw="500">WOS:</Text>}
                  name="identifiers.wosId"
                  label={t('citations.form.fields.identifiers.wosId')}
                  isSubmitting={isSubmitting}
                />
                <TextInputControl
                  leftSectionWidth="90px"
                  leftSection={<Text fw="500">SCOPUS:</Text>}
                  name="identifiers.scopusId"
                  label={t('citations.form.fields.identifiers.scopusId')}
                  isSubmitting={isSubmitting}
                />
              </Group>
              <TextInputControl
                leftSectionWidth="160px"
                leftSection={<Text>https://dx.doi.org/</Text>}
                name="identifiers.doi"
                label={t('citations.form.fields.identifiers.doi')}
                isSubmitting={isSubmitting}
              />
            </Stack>
            <Group align="flex-end">
              <NumberInputControl
                label={t('citations.form.fields.noOfAuthors')}
                name="noOfAuthors"
              />
              <PublishedAtControl
                label={t('publications.form.fields.publishedAt')}
                name="citationDate"
              />
            </Group>
          </Content>
          <Content type="full">
            <CitationPrestigeSelectControl
              label={t('citations.form.fields.citationPrestige')}
              name="citationPrestige"
            />
            <WosCategoryMultiSelectControl
              name="wosCategories"
              label={t('publications.form.fields.wosCategories')}
              isSubmitting={isSubmitting}
            />
            <WosIndexMultiSelectControl
              name="wosIndexes"
              label={t('publications.form.fields.wosIndexes')}
              isSubmitting={isSubmitting}
            />
          </Content>
          {/*<Content type="full"> TODO ADD AT LATER DATE*/}
          {/*  <AuthorListControl<CitationSchema>*/}
          {/*    name="authors"*/}
          {/*    isSubmitting={isSubmitting}*/}
          {/*    label={t('authors.form.title')}*/}
          {/*    renderMembership={({ membershipName }) => (*/}
          {/*      <CitationMembershipSelectControl*/}
          {/*        label={t('authors.form.fields.membership')}*/}
          {/*        name={membershipName}*/}
          {/*        isSubmitting={isSubmitting}*/}
          {/*        isDisabled={isSubmitting}*/}
          {/*      />*/}
          {/*    )}*/}
          {/*  />*/}
          {/*</Content>*/}
        </Stack>
      </Form>
    </FormProvider>
  );
}
